"use client"

import { getAuthHeader } from '../utils/auth'; // ★ 1. 認証ヘッダー取得関数をインポート
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { isEnglishOperatorMode } from '../utils/operatorMode';

const API_URL = "https://salesbreaker.jp";

// 通常の日付表示（2026/02/24）
const formatDate = (dateString, isEnglishBeta = false) => {
    if (!dateString) return '---';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '---' : date.toLocaleDateString(isEnglishBeta ? 'en-US' : 'ja-JP');
};

// 「〇月分」の表示（2026年2月）
const formatYearMonth = (dateString, isEnglishBeta = false) => {
    if (!dateString) return '---';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '---';
    if (isEnglishBeta) {
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    }
    return `${date.getFullYear()}年${date.getMonth() + 1}月`;
};

const normalizeCurrency = (invoice = {}) =>
    String(invoice.currency || invoice.billing_currency || 'jpy').toLowerCase();

const formatInvoiceAmount = (amount, invoice = {}, isEnglishBeta = false) => {
    const rawAmount = Number(amount || 0);
    const currency = normalizeCurrency(invoice);
    if (currency === 'usd') {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 2,
        }).format(rawAmount / 100);
    }
    return `¥${rawAmount.toLocaleString(isEnglishBeta ? 'en-US' : 'ja-JP')}`;
};

const INVOICES_COPY = {
    ja: {
        pageTitle: '利用明細',
        noticeTitle: '決済システムに関するご案内',
        noticeBody: '本ページには、Stripeで決済されたご利用料金のみが表示されます。旧決済システム（Square）をご利用のお客様で、明細が必要な場合は、お手数ですが運営事務局までお問い合わせください。',
        loading: '読み込み中...',
        empty: 'お支払い履歴はありません。',
        paid: '支払い済み',
        details: '詳細表示',
        issueDate: '発行日',
        invoiceId: '請求書ID',
        amountTaxIncluded: '金額 (税込)',
        status: 'ステータス',
        action: '操作',
        fetchError: '利用明細の取得に失敗しました。',
        modalTitle: '請求書詳細',
        invoiceTitle: 'ご請求書',
        issuer: '株式会社SalesBreaker',
        registrationNumber: '登録番号: T8120001283228',
        customerSuffix: '御中',
        billedAmount: 'ご請求金額',
        taxTarget: '(消費税10%対象)',
        description: '摘要',
        amountWithoutTax: '金額 (税抜)',
        serviceFee: (paidAt) => `Sales Breaker ご利用料金 (${formatYearMonth(paidAt)}分)`,
        subtotal: '小計 (税抜)',
        tax: '消費税 (10%)',
        total: '合計金額',
        paymentDestination: 'お振込先',
        paymentNote: '本請求書はクレジットカード決済のため、お振込みは不要です。',
        contact: 'お問い合わせ: info@salesbreaker.jp',
        print: '印刷 / PDF保存'
    },
    en: {
        pageTitle: 'Billing History',
        noticeTitle: 'About billing records',
        noticeBody: 'This page shows payments processed through Stripe. If you need records from a previous payment system, please contact the SalesBreaker team.',
        loading: 'Loading...',
        empty: 'No payment history yet.',
        paid: 'Paid',
        details: 'View Details',
        issueDate: 'Issue Date',
        invoiceId: 'Invoice ID',
        amountTaxIncluded: 'Amount',
        status: 'Status',
        action: 'Action',
        fetchError: 'Failed to load billing history.',
        modalTitle: 'Invoice Details',
        invoiceTitle: 'Invoice',
        issuer: 'SalesBreaker Inc.',
        registrationNumber: 'Registration No.: T8120001283228',
        customerSuffix: '',
        billedAmount: 'Amount Paid',
        taxTarget: 'Paid by card',
        description: 'Description',
        amountWithoutTax: 'Amount',
        serviceFee: (paidAt) => `SalesBreaker subscription fee (${formatYearMonth(paidAt, true)})`,
        subtotal: 'Subtotal',
        tax: 'Tax',
        total: 'Total',
        paymentDestination: 'Payment',
        paymentNote: 'This invoice was paid by credit card. No bank transfer is required.',
        contact: 'Contact: info@salesbreaker.jp',
        print: 'Print / Save PDF'
    }
};

// --- ▼▼▼ 新規追加：UIアイコン ▼▼▼ ---
const PrinterIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>;
const XIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const InfoIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>;


// --- ▼▼▼ 新規追加：請求書モーダルコンポーネント ▼▼▼ ---
const InvoiceModal = ({ invoice, clientInfo, onClose, isEnglishBeta = false }) => {
    const invoiceRef = useRef();
    const copy = isEnglishBeta ? INVOICES_COPY.en : INVOICES_COPY.ja;

    const handlePrint = () => {
        const printContents = invoiceRef.current.innerHTML;
        const originalContents = document.body.innerHTML;
        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;
        window.location.reload(); // スタイルを再適用するためにリロード
    };
    
    if (!invoice) return null;

    // 金額計算 (amount_paidは税込と想定)
    const totalAmount = invoice.amount_paid;
    const amountWithoutTax = Math.round(totalAmount / 1.1);
    const taxAmount = totalAmount - amountWithoutTax;
    const currency = normalizeCurrency(invoice);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-stretch z-50 p-0 print:hidden sm:items-center sm:p-4">
            <div className="bg-white rounded-none shadow-2xl w-full max-w-4xl max-h-none flex flex-col sm:max-h-[90vh] sm:rounded-lg">
                <div className="flex justify-between items-center p-4 border-b">
                    <h3 className="text-lg font-bold">{copy.modalTitle}</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200"><XIcon /></button>
                </div>
                <div ref={invoiceRef} className="p-4 overflow-y-auto sm:p-8" id="invoice-to-print">
                   {/* --- ▼ 請求書コンテンツ ▼ --- */}
                   <div className="p-4 bg-white border border-gray-200 sm:p-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                        <div className="flex flex-col justify-between gap-6 mb-8 sm:flex-row sm:items-start sm:mb-12">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-800 sm:text-3xl">{copy.invoiceTitle}</h1>
                                <p className="text-gray-500 mt-2">{copy.issueDate}: {formatDate(invoice.paid_at, isEnglishBeta)}</p>
                            </div>
                            <div className="text-left sm:text-right">
                                <p className="font-bold text-gray-700">{copy.issuer}</p>
                                <p className="text-sm text-gray-600">〒530-0001</p>
                                <p className="text-sm text-gray-600">大阪府大阪市北区梅田1丁目2番2号 大阪駅前第2ビル12-12</p>
                                <p className="text-sm text-gray-600 mt-2">{copy.registrationNumber}</p>
                            </div>
                        </div>

                        <div className="mb-12">
                            <p className="font-bold text-lg text-gray-800 border-b pb-2">{clientInfo?.name || '---'} {copy.customerSuffix}</p>
                        </div>
                        
                        <div className="text-left mb-8 sm:text-right">
                            <p className="font-bold text-xl text-gray-800 sm:text-2xl">{copy.billedAmount}: {formatInvoiceAmount(totalAmount, invoice, isEnglishBeta)}</p>
                            <p className="text-sm text-gray-600">{copy.taxTarget}</p>
                        </div>

                        <table className="w-full text-left border-collapse mb-8 text-sm sm:text-base">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="p-3 font-bold uppercase text-gray-600 text-sm border-b">{copy.description}</th>
                                    <th className="p-3 font-bold uppercase text-gray-600 text-sm border-b text-right">{copy.amountWithoutTax}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="p-3 text-gray-700 border-b">
                                        {copy.serviceFee(invoice.paid_at)}
                                    </td>
                                    <td className="p-3 text-gray-700 border-b text-right">
                                        {isEnglishBeta || currency !== 'jpy'
                                            ? formatInvoiceAmount(totalAmount, invoice, isEnglishBeta)
                                            : formatInvoiceAmount(amountWithoutTax, invoice, isEnglishBeta)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="flex justify-start mb-12 sm:justify-end">
                            <div className="w-full max-w-xs">
                                {!isEnglishBeta && currency === 'jpy' && (
                                    <>
                                        <div className="flex justify-between text-gray-700">
                                            <p>{copy.subtotal}</p>
                                            <p>{formatInvoiceAmount(amountWithoutTax, invoice, isEnglishBeta)}</p>
                                        </div>
                                        <div className="flex justify-between text-gray-700">
                                            <p>{copy.tax}</p>
                                            <p>{formatInvoiceAmount(taxAmount, invoice, isEnglishBeta)}</p>
                                        </div>
                                    </>
                                )}
                                <div className="flex justify-between font-bold text-gray-800 mt-2 pt-2 border-t">
                                    <p>{copy.total}</p>
                                    <p>{formatInvoiceAmount(totalAmount, invoice, isEnglishBeta)}</p>
                                </div>
                            </div>
                        </div>

                        <div className="border-t pt-8">
                            <p className="text-sm text-gray-600 font-bold mb-2">{copy.paymentDestination}</p>
                            <p className="text-sm text-gray-600">{copy.paymentNote}</p>
                            <p className="text-xs text-gray-500 mt-4">{copy.contact}</p>
                        </div>
                    </div>
                   {/* --- ▲ 請求書コンテンツ ▲ --- */}
                </div>
                <div className="flex justify-end p-4 border-t bg-gray-50">
                    <button onClick={handlePrint} className="flex w-full items-center justify-center bg-indigo-600 text-white font-bold py-2 px-4 rounded-md hover:bg-indigo-700 sm:w-auto">
                        <PrinterIcon />
                        {copy.print}
                    </button>
                </div>
            </div>
             {/* 印刷用スタイル */}
            <style jsx="true" global="true">{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    #invoice-to-print, #invoice-to-print * {
                        visibility: visible;
                    }
                    #invoice-to-print {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
};


// --- ▼▼▼ メインのページコンポーネント ▼▼▼ ---
function InvoicesPage() {
    const { clientId } = useOutletContext();
    const [invoices, setInvoices] = useState([]);
    const [clientInfo, setClientInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const isEnglishBeta = isEnglishOperatorMode();
    const copy = isEnglishBeta ? INVOICES_COPY.en : INVOICES_COPY.ja;

    const fetchInvoices = useCallback(async () => {
        if (!clientId || clientId === 'all') {
            setIsLoading(false);
            setInvoices([]); // 全クライアント選択時は何も表示しない
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_URL}/api/invoices?clientId=${clientId}`, {
                headers: getAuthHeader() // ★ 2. 認証ヘッダーを追加
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data.error || copy.fetchError);
            }
            setInvoices(data.invoices);
            setClientInfo(data.clientInfo);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [clientId, copy.fetchError]);

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);


    return (
        <div className="p-3 sm:p-4 md:p-8 bg-slate-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{copy.pageTitle}</h1>
                
                {/* --- ▼▼▼ ここからが修正箇所です ▼▼▼ --- */}
                <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start">
                    <InfoIcon className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="font-semibold text-blue-800">{copy.noticeTitle}</p>
                        <p className="text-sm text-blue-700 mt-1">
                            {copy.noticeBody}
                        </p>
                    </div>
                </div>
                {/* --- ▲▲▲ 修正はここまでです ▲▲▲ --- */}

                {error && <div className="bg-red-100 border-red-400 text-red-700 p-4 rounded-md mb-6">{error}</div>}

                <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <div className="divide-y divide-gray-100 md:hidden">
                            {isLoading ? (
                                <div className="px-4 py-12 text-center text-sm text-gray-500">{copy.loading}</div>
                            ) : invoices.length === 0 ? (
                                <div className="px-4 py-12 text-center text-sm text-gray-500">{copy.empty}</div>
                            ) : (
                                invoices.map((invoice) => (
                                    <div key={`${invoice.id}-mobile`} className="p-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="font-semibold text-gray-900">{formatDate(invoice.paid_at, isEnglishBeta)}</p>
                                                <p className="mt-1 break-all font-mono text-xs text-gray-500">{invoice.stripe_invoice_id}</p>
                                            </div>
                                            <span className="flex-shrink-0 rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">{copy.paid}</span>
                                        </div>
                                        <p className="mt-3 text-lg font-bold text-gray-900">{formatInvoiceAmount(invoice.amount_paid, invoice, isEnglishBeta)}</p>
                                        <button onClick={() => setSelectedInvoice(invoice)} className="mt-4 w-full rounded-md bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100">{copy.details}</button>
                                    </div>
                                ))
                            )}
                        </div>
                        <table className="hidden min-w-full divide-y divide-gray-200 md:table">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{copy.issueDate}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{copy.invoiceId}</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{copy.amountTaxIncluded}</th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{copy.status}</th>
                                    <th className="relative px-6 py-3"><span className="sr-only">{copy.action}</span></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {isLoading ? (
                                    <tr><td colSpan="5" className="text-center py-12 text-gray-500">{copy.loading}</td></tr>
                                ) : invoices.length === 0 ? (
                                    <tr><td colSpan="5" className="text-center py-12 text-gray-500">{copy.empty}</td></tr>
                                ) : (
                                    invoices.map((invoice) => (
                                        <tr key={invoice.id}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate(invoice.paid_at, isEnglishBeta)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{invoice.stripe_invoice_id}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">{formatInvoiceAmount(invoice.amount_paid, invoice, isEnglishBeta)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                    {copy.paid}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button onClick={() => setSelectedInvoice(invoice)} className="text-indigo-600 hover:text-indigo-900">
                                                    {copy.details}
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {selectedInvoice && (
                <InvoiceModal 
                    invoice={selectedInvoice} 
                    clientInfo={clientInfo}
                    onClose={() => setSelectedInvoice(null)} 
                    isEnglishBeta={isEnglishBeta}
                />
            )}
        </div>
    );
}

export default InvoicesPage;
